/**
 * Card Component
 *
 * A flexible card component with hover animations.
 * Follows shadcn/ui patterns for consistent styling.
 *
 * Features:
 * - Multiple variants (default, interactive, elevated)
 * - Smooth hover animations
 * - RTL support
 * - Composable with CardHeader, CardContent, CardFooter
 */

import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/utils/cn';

// =============================================================================
// Card Root
// =============================================================================

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Card style variant */
  variant?: 'default' | 'interactive' | 'elevated' | 'ghost';
  /** Whether the card is in an active/selected state */
  isActive?: boolean;
}

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', isActive = false, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-xl',
          'transition-all duration-200 ease-out',
          // Default variant
          variant === 'default' && [
            'bg-white border border-surface-100',
            'shadow-soft',
          ],
          // Interactive variant - with hover effects
          variant === 'interactive' && [
            'bg-white border border-surface-100',
            'shadow-soft',
            'cursor-pointer',
            'hover:shadow-soft-lg hover:border-primary-200',
            'hover:scale-[1.02]',
            'active:scale-[0.98]',
          ],
          // Elevated variant - more prominent shadow
          variant === 'elevated' && [
            'bg-white',
            'shadow-soft-lg',
          ],
          // Ghost variant - minimal styling
          variant === 'ghost' && [
            'bg-transparent',
          ],
          // Active state
          isActive && [
            'border-primary-300 bg-primary-50/50',
            'ring-2 ring-primary-500/20',
          ],
          className
        )}
        {...props}
      />
    );
  }
);

Card.displayName = 'Card';

// =============================================================================
// Card Header
// =============================================================================

export const CardHeader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1.5 p-6', className)}
    {...props}
  />
));

CardHeader.displayName = 'CardHeader';

// =============================================================================
// Card Title
// =============================================================================

export const CardTitle = forwardRef<
  HTMLHeadingElement,
  HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      'text-xl font-heading font-semibold leading-none tracking-tight text-surface-900',
      className
    )}
    {...props}
  />
));

CardTitle.displayName = 'CardTitle';

// =============================================================================
// Card Description
// =============================================================================

export const CardDescription = forwardRef<
  HTMLParagraphElement,
  HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn('text-sm text-surface-500', className)}
    {...props}
  />
));

CardDescription.displayName = 'CardDescription';

// =============================================================================
// Card Content
// =============================================================================

export const CardContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6 pt-0', className)} {...props} />
));

CardContent.displayName = 'CardContent';

// =============================================================================
// Card Footer
// =============================================================================

export const CardFooter = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center p-6 pt-0', className)}
    {...props}
  />
));

CardFooter.displayName = 'CardFooter';

// =============================================================================
// Exports
// =============================================================================

export default Card;
